/**
 * baglam.tsx
 *
 * NE İŞE YARAR : Oturumun tek sahibi — belirteci tutar, `/me`yi çözer, çıkışı yapar
 * KAYNAK SPEC  : 04-api-sozlesmesi.md X-02 (`GET /me`) · AU-06 (`POST /auth/logout`)
 *                §2.5 (Bearer) · §2.6 (`401` kodları) · ADR-008 §1 (izin kodları)
 *                15-arayuz-standardi.md §7.2 (arayüz izinleri nereden öğrenir)
 * BAĞLI OLDUĞU : src/api/istemci.ts · src/oturum/depo.ts
 * KULLANAN     : src/uygulama.tsx · src/ekran/**
 * DİKKAT       : 1) İZİNLER `X-02`DEN GELİR, ROL ADINDAN DEĞİL (`15` §7.2 · ADR-008 §1).
 *                   Arayüz *"bu düğme görünsün mü"* sorusunu **izin koduna** bakarak
 *                   cevaplar. Rol adına bakılsaydı yeni bir rol eklendiğinde her ekranın
 *                   ayrıca ele alınması gerekirdi.
 *                2) ARAYÜZDE GİZLEMEK GÜVENLİK DEĞİLDİR (`15` §7.3). Buradaki izin
 *                   listesi yalnız **ne gösterileceğini** belirler; erişimi sunucu
 *                   reddeder. Bu liste bir gün yanlış hesaplansa bile veri sızmaz.
 *                3) `401` GELDİĞİNDE OTURUM YEREL OLARAK DA KAPATILIR. Sunucu oturumu
 *                   iptal etmiş olabilir (ADR-008 §3 anlık iptal, `M2-10` cihaz kaldırma,
 *                   `AU-05` parola sıfırlama). Belirteç yerelde bırakılsaydı kullanıcı,
 *                   her isteği reddedilen bir *"açık"* oturumun içinde sıkışırdı.
 *                4) `/me` ÇAĞRISI AÇILIŞTA **BİR KEZ** YAPILIR. Her ekranın kendi
 *                   `/me`sini çekmesi, aynı sayfada iki farklı izin kümesinin geçerli
 *                   olabileceği bir an yaratırdı.
 *                5) ÇIKIŞTA SUNUCU HATASI YUTULUR. `AU-06` başarısız olsa da yerel
 *                   belirteç silinir: kullanıcı *"çıktım"* dediğinde ekranda kalması,
 *                   paylaşılan bir bilgisayarda en kötü sonuçtur. Sunucudaki oturum
 *                   zaten süresi dolduğunda kapanır (ADR-008 §5).
 *                6) CİHAZ SIRRINA ÇIKIŞTA DOKUNULMAZ (`04` AU-06). Çıkış yapmak cihaza
 *                   güvenmeyi bırakmak değildir; sonraki girişte 2FA sorulmaz.
 * SON GÜNCELLEME: 2026-08-31 13:05:00
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactElement, ReactNode } from 'react';

import { ApiHatasi } from '../api/hata';
import { istek, type IstekSecenekleri } from '../api/istemci';
import { oturumuOku, oturumuSil, oturumuYaz } from './depo';

/**
 * `X-02` yanıtındaki kullanıcı kaydı (`04` §4 registry · `03` §4 alan politikası).
 *
 * **Dizin imzası bilinçlidir.** Bu nesne de serileştiriciden geçiyor: çağıranın
 * profilinde olmayan alan yanıtta **hiç bulunmuyor** (`03` §2). Sabit anahtarlı bir
 * arayüz, olmayan alanı `undefined` gibi gösterip `15` §7.4'ün ayırmamızı istediği iki
 * durumu birleştirirdi. Aşağıdaki adlar **azami** kümedir, garanti değil; varlıkları
 * `alanVar()` ile sorulur.
 */
export interface BenKullanici extends Record<string, unknown> {
  readonly id: string;
  readonly email?: string | null;
  readonly phone_masked?: string | null;
  readonly status?: string;
  readonly auth_source?: string;
  readonly last_login_at?: string | null;
}

/** `X-02` yanıtındaki rol satırı — rol kodu ve hangi kurumda geçerli olduğu. */
export interface BenRol {
  readonly role_code: string;
  readonly organization_id: string;
  readonly organization_name: string;
}

/** `GET /me` yanıtının tamamı (`04` X-02). */
export interface Ben {
  readonly user: BenKullanici;
  readonly mfa_level: string;
  readonly roles: readonly BenRol[];
  /** İzin **kodları** — rol adı değil (başlıktaki not 1). */
  readonly permissions: readonly string[];
}

export type OturumDurumu = 'COZULUYOR' | 'ACIK' | 'KAPALI';

export interface OturumBaglami {
  readonly durum: OturumDurumu;
  readonly ben: Ben | null;
  /** Oturum açıldıktan sonra çağrılır: belirteç saklanır ve `/me` çözülür. */
  readonly oturumuBaslat: (belirtec: string, sonaErer: string) => Promise<void>;
  /** `AU-06` — sunucudaki oturumu kapatır ve yereli temizler. */
  readonly cikisYap: () => Promise<void>;
  /** Oturumlu istek: belirteci ekler, `401`de oturumu kapatır (başlıktaki not 3). */
  readonly cagir: <T>(secenek: Omit<IstekSecenekleri, 'belirtec'>) => Promise<T>;
  /** `15` §7.2 — ekranlar bunu sorar, rol adına bakmaz. */
  readonly izinVar: (kod: string) => boolean;
}

const Baglam = createContext<OturumBaglami | null>(null);

export function OturumSaglayici({ children }: { children: ReactNode }): ReactElement {
  const [durum, setDurum] = useState<OturumDurumu>('COZULUYOR');
  const [ben, setBen] = useState<Ben | null>(null);

  /**
   * Belirteç `ref`te tutulur, `state`te değil.
   *
   * `cagir` her istekte **o anki** belirteci okumak zorundadır; `state`ten okusaydı
   * `useCallback` kapanışı eski değeri taşır ve oturum açıldıktan hemen sonra yapılan
   * ilk çağrı belirteçsiz giderdi.
   */
  const belirtecRef = useRef<string | null>(null);

  const yereliTemizle = useCallback((): void => {
    belirtecRef.current = null;
    oturumuSil();
    setBen(null);
    setDurum('KAPALI');
  }, []);

  /** Belirteçli istek — `401` yerel oturumu da kapatır (başlıktaki not 3). */
  const cagir = useCallback(
    async <T,>(secenek: Omit<IstekSecenekleri, 'belirtec'>): Promise<T> => {
      try {
        return await istek<T>({ ...secenek, belirtec: belirtecRef.current });
      } catch (hata) {
        if (
          hata instanceof ApiHatasi &&
          (hata.kod === 'AUTH_REQUIRED' || hata.kod === 'SESSION_REVOKED')
        ) {
          yereliTemizle();
        }
        throw hata;
      }
    },
    [yereliTemizle],
  );

  /** `X-02` — kimlik, roller ve izin kodları. */
  const beniCoz = useCallback(async (): Promise<void> => {
    const yanit = await istek<Ben>({ yol: '/me', belirtec: belirtecRef.current });
    setBen(yanit);
    setDurum('ACIK');
  }, []);

  const oturumuBaslat = useCallback(
    async (belirtec: string, sonaErer: string): Promise<void> => {
      belirtecRef.current = belirtec;
      oturumuYaz({ belirtec, sonaErer });
      setDurum('COZULUYOR');
      try {
        await beniCoz();
      } catch (hata) {
        // Belirteç alındı ama `/me` çözülemedi: oturumu açık saymak, izinleri
        // bilinmeyen bir kullanıcıyı içeri almak olurdu.
        yereliTemizle();
        throw hata;
      }
    },
    [beniCoz, yereliTemizle],
  );

  const cikisYap = useCallback(async (): Promise<void> => {
    try {
      await istek({ yol: '/auth/logout', yontem: 'POST', belirtec: belirtecRef.current });
    } catch {
      // Başlıktaki not 5 — sunucu hatası çıkışı engellemez.
    } finally {
      yereliTemizle();
    }
  }, [yereliTemizle]);

  // Açılışta saklanmış belirteç varsa çözülür (başlıktaki not 4).
  useEffect(() => {
    const saklanan = oturumuOku();
    if (saklanan === null) {
      // Açılışta tarayıcı deposuyla eşitleme; depo okuması çizim sırasında
      // yapılamaz (68. adım).
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setDurum('KAPALI');
      return;
    }
    belirtecRef.current = saklanan.belirtec;
    void beniCoz().catch(() => {
      // Belirteç eskimiş ya da iptal edilmiş: `15` §9'un *"oturum kapandı"* durumu.
      yereliTemizle();
    });
  }, [beniCoz, yereliTemizle]);

  const izinVar = useCallback(
    (kod: string): boolean => ben?.permissions.includes(kod) === true,
    [ben],
  );

  const deger = useMemo<OturumBaglami>(
    () => ({ durum, ben, oturumuBaslat, cikisYap, cagir, izinVar }),
    [durum, ben, oturumuBaslat, cikisYap, cagir, izinVar],
  );

  return <Baglam.Provider value={deger}>{children}</Baglam.Provider>;
}

/**
 * Oturum bağlamını okur.
 *
 * Sağlayıcı dışında çağrılırsa **patlar**. `null` dönseydi her çağıran ayrıca
 * denetlemek zorunda kalır, denetlemeyen biri sessizce oturumsuz çalışırdı.
 */
export function useOturum(): OturumBaglami {
  const deger = useContext(Baglam);
  if (deger === null) {
    throw new Error('useOturum, OturumSaglayici dışında çağrıldı.');
  }
  return deger;
}
